// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline component for displaying asset actions.
 * This module provides a comprehensive view of an asset's
 * history, including transit events, ownership changes,
 * and other actions. It supports pagination through a
 * "See more" button and handles empty states.
 *
 * Key features:
 * - Action history display
 * - Event type handling
 * - Pagination support
 * - Empty state handling
 * - Timeline visualization
 */
import { Asset } from '@/asset/api/types';
import { AssetActionEvent } from '@/asset/components/action-event';
import { AssetOwnershipTimelineEvent } from '@/asset/components/asset-ownership-timeline-event';
import { AssetTransitEvent } from '@/asset/components/transit-event';
import { useAssetActions } from '@/asset/hooks/use-asset-actions';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';

/**
 * Props for configuring the AssetActionTimeline component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset whose actions are being displayed.
   * Used to fetch and display the action history.
   */
  asset: Asset;
}

/**
 * A timeline component for asset action history.
 * Displays a chronological list of asset actions including
 * transit events, ownership changes, and other activities.
 * Supports pagination and handles different event types
 * with appropriate visualizations.
 *
 * Features:
 * - Action history display
 * - Event type handling
 * - Pagination support
 * - Empty state handling
 * - Timeline visualization
 * - Grid layout
 *
 * Usage example:
 * ```tsx
 * function AssetHistory({ asset }: Props) {
 *   return (
 *     <Box>
 *       <Typography variant="h6">Activity History</Typography>
 *       <AssetActionTimeline asset={asset} />
 *     </Box>
 *   );
 * }
 * ```
 */
export function AssetActionTimeline(props: Props) {
  // Fetch asset actions
  const actions = useAssetActions(props.asset.key);

  // Pagination state
  const [count, setCount] = useState(5);

  // Get current page of data
  const timelineData = actions.slice(0, count);
  const hasMoreData = count < actions.length;

  /**
   * Handles "See more" button clicks.
   * Increases the number of visible actions by 5.
   */
  const handleClickMore = () => {
    setCount((currentCount) => currentCount + 5);
  };

  return (
    <Stack>
      {timelineData.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No activity
        </Typography>
      ) : (
        <Box
          alignSelf="start"
          display="grid"
          gridTemplateColumns="auto 1fr"
          columnGap={2}
        >
          {timelineData.map((item) => {
            // Handle transit events
            if ('events' in item) {
              return (
                <AssetTransitEvent
                  key={item.id}
                  eventGroup={item}
                  asset={props.asset}
                />
              );
            }

            // Handle ownership events
            if ('customer' in item) {
              return (
                <AssetOwnershipTimelineEvent
                  key={item.id}
                  event={item}
                  assetKey={props.asset.key}
                  costCurrencyCode={props.asset.accounting.costCurrencyCode}
                />
              );
            }

            // Handle other action events
            return <AssetActionEvent key={item.id} event={item} />;
          })}
        </Box>
      )}
      {hasMoreData && (
        <Button
          size="small"
          sx={{ alignSelf: 'start' }}
          onClick={handleClickMore}
        >
          See more
        </Button>
      )}
    </Stack>
  );
}
